import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiErrorMessage } from '../api/client';
import * as formulationsApi from '../api/formulationsApi';
import { EmptyState, ErrorBanner, LoadMore, Modal, TableSkeleton } from './ui';
import { FormulationIO } from './FormulationDetail';
import { IconChevronDown } from './icons';
import { ConversionStatus, FormulationUser } from '../types/formulation';
import { formatDateTime } from '../utils/format';

/**
 * The people behind "Generated a formula" — everyone who finished the quiz and
 * was shown a formulation, and what became of it.
 *
 * SIGNED-IN generations only, so this total sits at or below the funnel bar
 * that opens it: the bar counts anonymous reveals too, and there is nobody to
 * put in a row for those.
 */

const CONVERSION: Record<ConversionStatus, { label: string; className: string }> = {
  ordered: { label: '✓ Ordered',     className: 'border-ok/40 bg-ok/10 text-ok' },
  in_cart: { label: 'In Cart',       className: 'border-warn/40 bg-warn/10 text-warn' },
  none:    { label: 'Not converted', className: 'border-edge bg-edge/30 text-sub' },
};

function ConversionBadge({ status }: { status: ConversionStatus }) {
  const tone = CONVERSION[status] ?? CONVERSION.none;
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium ${tone.className}`}
    >
      {tone.label}
    </span>
  );
}

export function FormulationUsersModal({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<FormulationUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await formulationsApi.getFormulationUsers({ page });
      // Page 1 replaces (first load and retry); later pages append.
      setRows((prev) => (page === 1 ? data.users : [...prev, ...data.users]));
      setTotal(data.pagination.total);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Modal
      open
      title={`Users who generated a formula${total ? ` — ${total}` : ''}`}
      onClose={onClose}
      size="xl"
      bodyClassName=""
    >
      {error ? (
        <div className="p-5">
          <ErrorBanner message={error} onRetry={load} />
        </div>
      ) : loading && rows.length === 0 ? (
        <TableSkeleton rows={5} />
      ) : rows.length === 0 ? (
        <EmptyState
          message="Nobody has generated a formula while signed in"
          hint="Anonymous reveals aren't listed here — there's nobody to attribute them to"
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-edge">
                  <th className="th">User Name</th>
                  <th className="th">Email</th>
                  <th className="th">Cluster</th>
                  <th className="th">Generated At</th>
                  <th className="th">Converted?</th>
                  <th className="th">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {rows.map((u) => {
                  const open = openId === u.formulation_id;
                  return [
                    <tr
                      key={u.formulation_id}
                      onClick={() => setOpenId(open ? null : u.formulation_id)}
                      className="cursor-pointer transition-colors hover:bg-edge/20"
                    >
                      <td className="td">
                        {/* stopPropagation so navigating away isn't also an
                            expand on the row underneath the pointer. */}
                        <Link
                          to={`/users/${u.user_id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="font-medium text-gold hover:underline"
                        >
                          {u.user_name?.trim() || 'Unnamed'}
                        </Link>
                      </td>
                      <td className="td text-sub">{u.user_email || '—'}</td>
                      <td className="td">{u.cluster_label || '—'}</td>
                      <td className="td text-sub">{formatDateTime(u.generated_at)}</td>
                      <td className="td">
                        <ConversionBadge status={u.conversion_status} />
                      </td>
                      <td className="td">
                        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-sub">
                          {open ? 'Hide' : 'View'} Formulation
                          <IconChevronDown
                            className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
                          />
                        </span>
                      </td>
                    </tr>,
                    open && (
                      <tr key={`${u.formulation_id}-detail`} className="bg-edge/10">
                        <td className="px-5 py-4" colSpan={6}>
                          <FormulationIO
                            request={u.request}
                            response={u.response}
                            clusterLabel={u.cluster_label}
                          />
                        </td>
                      </tr>
                    ),
                  ];
                })}
              </tbody>
            </table>
          </div>
          <LoadMore
            shown={rows.length}
            total={total}
            loading={loading}
            onMore={() => setPage((p) => p + 1)}
          />
          <p className="border-t border-edge px-5 py-3 text-xs text-sub">
            Signed-in generations only, newest first. "Ordered" means the formula was paid
            for; "In Cart" means it is still sitting in a bag.
          </p>
        </>
      )}
    </Modal>
  );
}
